import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatDkk } from "@/lib/api";
import { Plus, Trash2, X } from "lucide-react";
import { expertRulesPath } from "@/lib/businessRules";

const toLocalDateTimeInput = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const nextHalfHour = (from = new Date()) => {
  const next = new Date(from);
  next.setSeconds(0, 0);
  const minutes = next.getMinutes();
  next.setMinutes(minutes < 30 ? 30 : 60);
  return next;
};

const defaultProposedTime = () => {
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
  return toLocalDateTimeInput(nextHalfHour(tomorrow));
};

const durationLabel = (minutes) => {
  const value = Number(minutes) || 0;
  if (value < 60) return `${value} minutter`;
  const hours = Math.floor(value / 60);
  const remainder = value % 60;
  return remainder ? `${hours} t. ${remainder} min.` : `${hours} ${hours === 1 ? "time" : "timer"}`;
};

const durationCalculation = (minutes, hourlyRate) => {
  return `${durationLabel(minutes)} × ${formatDkk(hourlyRate || 0)}/time`;
};

export default function ProposalForm({ request, minPrice = 0, hourlyRate = 0, onClose, onSubmitted }) {
  const minimum = Math.max(0, Number(minPrice) || 0);
  const [model, setModel] = useState("fixed");
  const [fixedPrice, setFixedPrice] = useState(minimum || 349);
  const [rate, setRate] = useState(Number(hourlyRate) || 450);
  const [serviceTitle, setServiceTitle] = useState("");
  const [expectedMinutes, setExpectedMinutes] = useState(Number(request?.duration_min) || 30);
  const [scope, setScope] = useState("");
  const [note, setNote] = useState("");
  const [availabilityType, setAvailabilityType] = useState("scheduled");
  const [proposedTimes, setProposedTimes] = useState([defaultProposedTime()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const hourlyTotal = useMemo(
    () => Math.round((Number(rate) || 0) * (Number(expectedMinutes) || 0) / 60),
    [rate, expectedMinutes],
  );

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (serviceTitle.trim().length < 3) return setError("Skriv en kort titel på ydelsen.");
    if (!scope.trim()) return setError("Beskriv hvad dit prisforslag omfatter.");
    if (!expectedMinutes || Number(expectedMinutes) <= 0) return setError("Angiv et gyldigt forventet tidsforbrug.");
    if (model === "fixed" && (!fixedPrice || fixedPrice < minimum)) {
      return setError(`Den faste pris skal være mindst din fra-pris på ${minimum} kr.`);
    }
    if (model === "hourly" && !rate) {
      return setError("Angiv en gyldig timepris.");
    }
    if (model === "hourly" && hourlyTotal < minimum) {
      return setError(`Den samlede pris skal være mindst din fra-pris på ${minimum} kr.`);
    }
    if (availabilityType === "scheduled" && (
      proposedTimes.length < 1
      || proposedTimes.length > 3
      || proposedTimes.some((value) => !value || new Date(value).getTime() <= Date.now())
    )) {
      return setError("Vælg mellem 1 og 3 gyldige tidspunkter i fremtiden.");
    }
    setBusy(true);
    try {
      await api.post(`/problems/${request.id}/proposals`, {
        price_model: model,
        service_title: serviceTitle.trim(),
        fixed_price: model === "fixed" ? Number(fixedPrice) : null,
        hourly_rate: model === "hourly" ? Number(rate) : null,
        estimated_minutes_min: Number(expectedMinutes),
        estimated_minutes_max: Number(expectedMinutes),
        scope: scope.trim(),
        note: note.trim() || null,
        availability_type: availabilityType,
        proposed_times: availabilityType === "scheduled"
          ? proposedTimes.map((value) => new Date(value).toISOString())
          : [],
      });
      onSubmitted?.();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Prisforslaget kunne ikke sendes.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-ink/40 flex items-center justify-center p-4" onClick={onClose}>
      <form className="card-outline p-5 max-w-lg w-full max-h-[92vh] overflow-y-auto" onSubmit={submit} onClick={(event) => event.stopPropagation()} data-testid="proposal-form">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-brand-muted">Prisforslag til kunden</div>
            <h3 className="font-display text-lg font-bold text-brand-ink">{request?.title}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Luk"><X size={18}/></button>
        </div>

        <label className="mt-4 block">
          <span className="text-sm font-medium text-brand-ink">Ydelse</span>
          <input required maxLength={120} value={serviceTitle} onChange={(event) => setServiceTitle(event.target.value)} className="mt-1 w-full card-outline px-3 py-2.5" placeholder="Fx Fejlfinding af Wi-Fi" data-testid="proposal-service-title"/>
          <span className="mt-1 block text-xs text-brand-muted">Skriv kort hvad kunden køber.</span>
        </label>

        <div className="mt-4 grid grid-cols-2 gap-2">
          {[["fixed", "Samlet fast pris"], ["hourly", "Pris efter tid"]].map(([value, label]) => (
            <button type="button" key={value} onClick={() => setModel(value)} className={`rounded-xl border px-4 py-3 text-sm font-medium ${model === value ? "bg-brand-green text-white border-brand-green" : "bg-white border-brand-line text-brand-ink"}`}>
              {label}
            </button>
          ))}
        </div>

        {model === "fixed" ? (
          <label className="block mt-4">
            <span className="text-sm font-medium text-brand-ink">Fast pris (kr.)</span>
            <input type="number" min={Math.max(1, minimum)} value={fixedPrice} onChange={(event) => setFixedPrice(event.target.value)} className="mt-1 w-full card-outline px-3 py-2.5"/>
            <span className="block mt-1 text-xs text-brand-muted">Hele den beskrevne ydelse koster {formatDkk(fixedPrice || 0)}.</span>
          </label>
        ) : (
          <div className="mt-4">
            <label className="block">
              <span className="text-sm font-medium text-brand-ink">Timepris (kr.)</span>
              <input type="number" min="1" value={rate} onChange={(event) => setRate(event.target.value)} className="mt-1 w-full card-outline px-3 py-2.5"/>
            </label>
          </div>
        )}

        <label className="mt-4 block">
          <span className="text-sm font-medium text-brand-ink">Forventet tidsforbrug</span>
          <select value={expectedMinutes} onChange={(event) => setExpectedMinutes(Number(event.target.value))} className="mt-1 w-full card-outline bg-white px-3 py-2.5" data-testid="proposal-expected-time">
            {[[15, "15 minutter"], [30, "30 minutter"], [45, "45 minutter"], [60, "1 time"], [90, "1 time og 30 minutter"], [120, "2 timer"], [180, "3 timer"], [240, "4 timer"]].map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>

        <div className="mt-4 overflow-hidden rounded-xl border border-brand-line bg-white" data-testid="proposal-price-preview">
          <div className="border-b border-brand-line bg-brand-wash px-4 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-brand-body">Tilbud</div>
          <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 px-4 py-3 text-sm">
            <div className="min-w-0"><div className="font-medium text-brand-ink">{serviceTitle.trim() || "Din ydelse"}</div><div className="mt-0.5 text-xs text-brand-muted">Forventet tid: {durationLabel(expectedMinutes)}</div></div>
            <div className="text-right"><div className="font-semibold text-brand-ink">{model === "fixed" ? formatDkk(fixedPrice || 0) : formatDkk(hourlyTotal)}</div>{model === "hourly" && <div className="mt-0.5 text-xs text-brand-muted">{durationCalculation(expectedMinutes, rate)}</div>}</div>
          </div>
          <div className="flex items-center justify-between border-t border-brand-line px-4 py-3"><span className="text-sm font-semibold text-brand-ink">Samlet pris</span><span className="font-display text-xl font-bold text-brand-green">{model === "fixed" ? formatDkk(fixedPrice || 0) : formatDkk(hourlyTotal)}</span></div>
        </div>

        <div className="mt-4" data-testid="proposal-availability">
          <div className="text-sm font-medium text-brand-ink">Hvornår kan du hjælpe?</div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {[["asap", "Så hurtigt som muligt"], ["scheduled", "Vælg konkrete tider"]].map(([value, label]) => (
              <button
                type="button"
                key={value}
                onClick={() => setAvailabilityType(value)}
                className={`rounded-xl border px-3 py-2.5 text-sm ${availabilityType === value ? "bg-brand-green text-white border-brand-green" : "bg-white border-brand-line text-brand-ink"}`}
                data-testid={`availability-${value}`}
              >
                {label}
              </button>
            ))}
          </div>
          {availabilityType === "asap" && (
            <p className="mt-2 rounded-lg bg-brand-soft px-3 py-2 text-xs leading-5 text-brand-body">
              Kunden vælger ikke et løst tidspunkt. Når betalingen er gennemført, foreslår du den første konkrete tid du kan.
            </p>
          )}

          {availabilityType === "scheduled" && (
            <div className="mt-3" data-testid="proposal-times">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-brand-ink">Foreslåede tidspunkter</div>
                  <div className="text-xs text-brand-muted">Tilføj 1–3 tider hvor du kan hjælpe kunden.</div>
                </div>
                {proposedTimes.length < 3 && (
                  <button
                    type="button"
                    onClick={() => setProposedTimes((current) => [...current, defaultProposedTime()])}
                    className="inline-flex items-center gap-1 text-xs text-brand-green"
                    data-testid="add-proposed-time"
                  >
                    <Plus size={13}/> Tilføj tid
                  </button>
                )}
              </div>
              <div className="mt-2 space-y-2">
                {proposedTimes.map((value, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="datetime-local"
                      step="1800"
                      value={value}
                      min={toLocalDateTimeInput(nextHalfHour())}
                      onChange={(event) => setProposedTimes((current) => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))}
                      className="w-full card-outline px-3 py-2.5"
                      aria-label={`Foreslået tidspunkt ${index + 1}`}
                      data-testid={`proposed-time-${index}`}
                    />
                    {proposedTimes.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setProposedTimes((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                        className="p-2 text-[#991B1B]"
                        aria-label="Fjern tidspunkt"
                      >
                        <Trash2 size={16}/>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <label className="block mt-4">
          <span className="text-sm font-medium text-brand-ink">Hvad er inkluderet i prisforslaget?</span>
          <textarea required rows={4} value={scope} onChange={(event) => setScope(event.target.value)} className="mt-1 w-full card-outline px-3 py-2.5 resize-y" placeholder="Beskriv tydeligt hvad du vil hjælpe med og hvad prisforslaget omfatter."/>
        </label>
        <label className="block mt-4">
          <span className="text-sm font-medium text-brand-ink">Note til kunden <span className="font-normal text-brand-muted">(valgfrit)</span></span>
          <textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} className="mt-1 w-full card-outline px-3 py-2.5 resize-y"/>
        </label>

        {error && <div className="mt-3 text-sm text-[#991B1B]">{error}</div>}
        <p className="mt-4 rounded-lg bg-[#FFF7E8] p-3 text-xs leading-5 text-[#6F4B16]">Pris og omfang er bindende for den beskrevne opgave når kunden vælger dit forslag. Ved at sende accepterer du <Link to={expertRulesPath} target="_blank" className="font-semibold underline">reglerne for opgaver og betaling</Link>.</p>
        <button type="submit" disabled={busy} className="btn-primary w-full mt-3 disabled:opacity-50">{busy ? "Sender…" : "Send prisforslag"}</button>
      </form>
    </div>
  );
}

